import React, { useEffect, useState } from 'react';
import { useParams, Navigate } from 'react-router-dom';
import { Copy, Download, Calendar, Tag } from 'lucide-react';
import { useData } from '../context/DataContext';
import MultiSectionPage from '../components/MultiSectionPage';
import { highlightSQL } from '../utils/sqlHighlight';

const ScriptPage = () => {
  const { id } = useParams();
  const { scripts, trackPageView, trackDownload } = useData();
  const [copied, setCopied] = useState(false);
  
  const script = scripts.find(s => s.id === id);

  useEffect(() => {
    if (script) {
      trackPageView(`script-${script.id}`);
    }
  }, [script, trackPageView]);

  if (!script) {
    return <Navigate to="/" replace />;
  }

  if (script.isMultiSection) {
    return <MultiSectionPage script={script} />;
  }

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(script.content);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy text: ', err);
    }
  };

  const handleDownload = () => {
    const element = document.createElement('a');
    const file = new Blob([script.content], { type: 'text/plain' });
    element.href = URL.createObjectURL(file);
    element.download = `${script.title.replace(/[^a-z0-9]/gi, '_').toLowerCase()}.sql`;
    document.body.appendChild(element);
    element.click();
    document.body.removeChild(element);
    trackDownload(script.id);
  };

  return (
    <div className="page-container">
      <div className="script-header">
        <h1 className="script-title">{script.title}</h1>
        <div className="script-meta">
          <span style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
            <Tag size={16} />
            {script.category} - {script.subcategory}
          </span>
          <span style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
            <Calendar size={16} />
            {new Date(script.createdAt).toLocaleDateString()}
          </span>
        </div>
      </div>

      <div className="script-content">
        <div className="script-description">
          {script.description}
        </div>

        <div className="code-block">
          <div className="code-header">
            <span className="code-title">SQL Script</span>
            <div className="code-actions">
              <button 
                onClick={handleCopy} 
                className={`btn ${copied ? 'btn-success' : 'btn-primary'}`}
              >
                <Copy size={16} />
                {copied ? 'Copied!' : 'Copy'}
              </button>
              <button onClick={handleDownload} className="btn btn-secondary">
                <Download size={16} />
                Download
              </button>
            </div>
          </div>
          <div className="code-content" dangerouslySetInnerHTML={{ __html: highlightSQL(script.content) }}>
          </div>
        </div>

        <div style={{ marginTop: '30px', padding: '20px', background: '#f8f9fa', borderRadius: '8px', border: '1px solid #e9ecef' }}>
          <h3 style={{ marginBottom: '15px', color: '#495057' }}>Usage Notes</h3>
          <ul style={{ color: '#6c757d', lineHeight: '1.6' }}>
            <li>Always test scripts in a development environment before running in production</li>
            <li>Review and understand the script logic before execution</li>
            <li>Ensure you have appropriate permissions to run the script</li>
            <li>Consider the impact on system performance during execution</li>
            <li>Take necessary backups before making any changes</li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default ScriptPage;